<script lang="ts">
import { getConcept, getConcepts } from '../../_shared/hooks/useConcepts';
import { getPages } from '../../_shared/hooks/usePages';
import { getCycles } from '../../_shared/hooks/useCycles';
import { textReplace } from '../../_shared/hooks/useTextHelper';
import { getDictionary } from '../../_shared/hooks/useDictionary';
import {
  cycleDateRange,
  cycleDay,
  cycleDuration,
  isCycleRunning,
  isCycleStarted,
  isPublicVisible,
  cycleTimeRange,
} from '../../_shared/hooks/useCycleHelper';
import { Cycle } from '../../_shared/types/cycle.types';
import NewsletterAndContactButton from '../../_shared/components/buttons/NewsletterAndContactButton.vue';
import { Page } from '../../_shared/types/page.types';
import { Concept } from '../../_shared/types/concept.types';

const CourseBox = {
  props: ['cycle', 'concept'],
  methods: {
    isCycleRunning,
    cycleDateRange,
    cycleDay,
    cycleTimeRange,
    cycleDuration,
    createParticipantLabel(cycle: Cycle) {
      if (this.concept.maxParticipants - cycle.participantCount === 1) {
        return '<div class="label label-danger">noch 1 Platz frei</div>';
      }

      if (
        cycle.participantCount >= this.concept.maxParticipants &&
        isCycleRunning(cycle)
      ) {
        return '<div class="label label-default">ausgebucht</div>';
      }

      if (cycle.participantCount >= this.concept.maxParticipants) {
        return '<div class="label label-warning">Warteliste</div>';
      }

      if (cycle.participantCount >= this.concept.maxParticipants / 2) {
        return `<div class="label label-info">noch ${
          this.concept.maxParticipants - cycle.participantCount
        } Plätze frei</div>`;
      }

      return '<div class="label label-primary">Plätze frei</div>';
    },
  },
  template: `
    <div
      v-if="cycle"
      class="panel panel-dark"
    >
      <div class="panel-body">
        <div class="cycle-facts">
          <div>
            <i class="fa fa-calendar-o"></i>
            {{ cycleDateRange(cycle) }} ({{ cycleDay(cycle) }})
          </div>
          <div>
            <i class="fa fa-hourglass-start"></i>
            {{ concept.weekCount }} Wochen
          </div>
          <div>
            <i class="fa fa-clock-o"></i>
            {{ cycleTimeRange(cycle) }} Uhr ({{ cycleDuration(cycle) }} Minuten)
          </div>
          <div v-if="cycle.address">
            <i class="fa fa-map-pin"></i>
            {{ cycle.address }}
          </div>
          <div>
            <i class="fa fa-money"></i>
            {{ concept.fee }} €
          </div>
          <div v-html="createParticipantLabel(cycle)"></div>
        </div>

        {{ cycle.note }}
      </div>
      <div class="panel-footer" style="padding: 0">
        <button v-if="isCycleRunning(cycle)" class="btn btn-default" style="width: 100%" disabled>gestartet</button>
        <a
          v-else
          class="btn btn-warning btn-lg"
          style="width: 100%"
          :href="'./anmeldung?id=' + cycle._id"
        >anmelden</a
        >
      </div>
    </div>
  `,
};

export default {
  data(): {
    page: Page | null;
    concept: Concept | null;
    cycles: Cycle[] | null;
    dictionary: any;
    similarConcepts: Concept[] | null;
  } {
    return {
      page: null,
      concept: null,
      cycles: null,
      dictionary: null,
      similarConcepts: null,
    };
  },
  components: {
    NewsletterAndContactButton,
    CourseBox,
  },
  computed: {
    newCycles() {
      if (!this.cycles || !this.concept) {
        return [];
      }

      return this.cycles.filter(
        (cycle: Cycle) =>
          !isCycleStarted(cycle) && isPublicVisible(cycle, this.concept)
      );
    },
    runningCycles() {
      if (!this.cycles || !this.concept) {
        return [];
      }

      return this.cycles.filter((cycle) => isCycleRunning(cycle));
    },
  },
  methods: {
    textReplace,
  },
  async mounted() {
    const conceptUrl = window.location.pathname;

    this.page = await getPages({ urls: [conceptUrl], skip: 0, take: 1 }).then(
      ([page]) => page
    );

    if (!this.page) {
      return;
    }

    this.concept = await getConcept(this.page.id);

    if (!this.concept) {
      return;
    }

    getDictionary({ course: this.concept }).then(
      (dictionary) => (this.dictionary = dictionary)
    );

    this.cycles = await getCycles(
      { courseId: this.concept._id },
      {
        fields: {
          address: 1,
          active: 1,
          courseId: 1,
          dateFrom: 1,
          dateTo: 1,
          internPlanningDate: 1,
          leader: 1,
          publicPlanningDate: 1,
          note: 1,
        },
      }
    );

    getConcepts(
      {
        active: true,
        recording: !!this.concept.recording,
        webinar: !!this.concept.webinar,
      },
      { with: ['page'] }
    ).then((concepts) => {
      this.similarConcepts = concepts.filter(
        (concept) => concept._id !== this.concept._id && concept.page?.url
      );
    });
  },
};
</script>

<template>
  <div id="main-content" v-if="concept">
    <div
      v-if="concept.textDescription"
      v-html="textReplace(concept.textDescription, dictionary)"
    />

    <div v-if="!concept.recording" class="text-center">
      <NewsletterAndContactButton></NewsletterAndContactButton>
    </div>

    <div
      v-if="concept.textDescription2"
      v-html="textReplace(concept.textDescription2, dictionary)"
    />

    <div
      v-if="concept.recording"
      class="text-center"
      style="margin-bottom: 20px"
    >
      <a
        :href="`/kurse/anmeldung?course=${concept._id}`"
        class="btn btn-lg btn-warning"
        style="width: 200px"
        >Jetzt anmelden</a
      >
    </div>
    <template v-else>
      <template
        v-if="
          (newCycles && newCycles.length) ||
          (runningCycles && runningCycles.length)
        "
      >
        <div class="cycles">
          <CourseBox
            v-for="cycle in newCycles"
            :cycle="cycle"
            :concept="concept"
          ></CourseBox>
        </div>

        <div class="cycles">
          <CourseBox
            v-for="cycle in runningCycles"
            :cycle="cycle"
            :concept="concept"
          ></CourseBox>
        </div>
      </template>

      <div v-if="!cycles" class="big-loader fa fa-spinner fa-pulse fa-2x"></div>

      <p
        v-else-if="!cycles.length && concept.textNoCurrentCycles"
        v-html="textReplace(concept.textNoCurrentCycles, dictionary)"
      ></p>

      <div
        v-if="concept.textAlternatives"
        v-html="concept.textAlternatives"
        style="margin-bottom: 20px"
      ></div>
    </template>

    <div
      class="row"
      v-if="similarConcepts && similarConcepts.length"
      style="margin-bottom: 20px"
    >
      <div class="col-xs-12">
        <h3>Weitere beliebte Präventionskurse</h3>
      </div>

      <div class="col-xs-12" style="display: flex; flex-wrap: wrap; gap: 8px">
        <a
          v-for="similarConcept in similarConcepts"
          class="btn btn-warning btn-lg"
          :href="`/${similarConcept.page.url}`"
          role="button"
          style="flex: 1 1 auto"
        >
          {{ similarConcept.page.navigationTitle }}
        </a>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.cycles {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  margin-bottom: 18px;
}

:deep(.panel) {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

:deep(.cycle-facts) {
  font-weight: bold;
  margin-bottom: 8px;
}

:deep(.cycle-facts > div) {
  text-align: left;
}

:deep(.cycle-facts i) {
  margin-right: 4px;
  text-align: center;
  width: 16px;
}

:deep(.panel-footer .btn) {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}
</style>
